import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

export interface UserData {
  iduser?: string;
  idUser?: string;
  id?: string;
  idGuardian?: string;
  listStudent?: any[];
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private userDataSubject = new BehaviorSubject<UserData | null>(null);
  private currentUrlSubject = new BehaviorSubject<string>('/accueil');

  isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  userData$ = this.userDataSubject.asObservable();

  constructor() {
    this.restoreSession();
  }

  /**
   * Restore session from localStorage on app initialization
   */
  restoreSession(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
      const userDataStr = localStorage.getItem('userData');
      
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        const hasValidUser = !!(userData && (userData.iduser || userData.idUser || userData.id));
        
        if (hasValidUser) {
          this.userDataSubject.next(userData);
          this.isAuthenticatedSubject.next(true);
          
          // Restore last URL if available
          const lastUrl = sessionStorage.getItem('lastUrl');
          if (lastUrl) {
            this.currentUrlSubject.next(lastUrl);
          }
          
          return true;
        }
      }

      if (isLoggedIn) {
        this.isAuthenticatedSubject.next(true);
        return true;
      }

      return false;
    } catch (e) {
      console.warn('Error restoring session:', e);
      return false;
    }
  }

  /**
   * Save current URL to sessionStorage for refresh handling
   */
  saveCurrentUrl(url: string): void {
    if (isPlatformBrowser(this.platformId) && url && !url.includes('/home')) {
      try {
        sessionStorage.setItem('lastUrl', url);
        this.currentUrlSubject.next(url);
      } catch (e) {
        console.warn('Error saving URL:', e);
      }
    }
  }

  /**
   * Get the last saved URL
   */
  getLastUrl(): string {
    if (isPlatformBrowser(this.platformId)) {
      return sessionStorage.getItem('lastUrl') || '/accueil';
    }
    return '/accueil';
  }

  /**
   * Clear the saved URL
   */
  clearLastUrl(): void {
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem('lastUrl');
    }
  }

  /**
   * Login user and store session
   */
  login(userData: UserData): void {
    // ensure nameOrg exists even if backend didn't supply it for guardians
    if (!userData['nameOrg'] && userData.listStudent?.length) {
      // take first student's org as representative
      userData['nameOrg'] = userData.listStudent[0].nameOrg;
    }

    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem('userData', JSON.stringify(userData));
        localStorage.setItem('isLoggedIn', 'true');
        
        if (userData.listStudent?.length) {
          const guardianId = userData.idGuardian;
          const patchedStudents = userData.listStudent.map((student: any) => ({
            ...student,
            idGuardian: guardianId
          }));
          localStorage.setItem('studentList', JSON.stringify(patchedStudents));
        }
        
        this.userDataSubject.next(userData);
        this.isAuthenticatedSubject.next(true);
      } catch (e) {
        console.warn('Error saving login data:', e);
      }
    }
  }

  /**
   * Logout user and clear session
   */
  logout(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.removeItem('userData');
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('studentList');
        sessionStorage.removeItem('lastUrl');
        sessionStorage.removeItem('pendingCreds');
      } catch (e) {
        console.warn('Error clearing session:', e);
      }
    }
    
    this.userDataSubject.next(null);
    this.isAuthenticatedSubject.next(false);
    this.router.navigate(['/home']);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  /**
   * Get current user data
   */
  getUserData(): UserData | null {
    return this.userDataSubject.value;
  }

  /**
   * Check for pending credentials (from URL params)
   */
  hasPendingCredentials(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      return !!sessionStorage.getItem('pendingCreds');
    }
    return false;
  }
}
